import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2 as CheckCircle, Circle, ExternalLink as ArrowSquareOut, AlertTriangle, ChevronRight } from "lucide-react";

export default function Onboarding() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const brandId = params.get("brand_id");
  const [brands, setBrands] = useState([]);
  const [state, setState] = useState(null);

  useEffect(() => {
    api.listBrands().then(setBrands);
  }, []);
  useEffect(() => {
    if (brandId) api.onboarding(brandId).then(setState); else setState(null);
  }, [brandId]);

  const pickBrand = (bid) => {
    setParams({ brand_id: bid });
  };

  const setHasSubstack = async (val) => {
    setState((s) => ({ ...s, has_substack: val }));
    await api.updateOnboarding({ has_substack: val }, brandId);
  };

  const toggleStep = async (idx) => {
    const completed = new Set(state.completed_steps || []);
    const sid = state.steps[idx].id;
    if (completed.has(sid)) completed.delete(sid); else completed.add(sid);
    const next = Array.from(completed);
    setState({ ...state, completed_steps: next, step: idx });
    await api.updateOnboarding({ completed_steps: next, step: idx }, brandId);
  };

  // Brand-picker view
  if (!brandId) {
    return (
      <div data-testid="onboarding-picker" className="max-w-3xl">
        <div className="mb-8">
          <div className="text-eyebrow mb-3">Setup Wizard</div>
          <h1 className="font-display font-black text-5xl tracking-tighter">Substack Setup</h1>
          <p className="text-[#52525B] mt-3">Each brand needs its OWN Substack publication. Pick a brand to walk through its setup.</p>
        </div>
        <div className="border-l-4 border-[#F59E0B] bg-[#FFF7ED] p-4 mb-6 text-sm" data-testid="separate-publications-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 text-[#F59E0B] shrink-0" />
            <div>
              <strong>Never mix brands on one Substack.</strong> Create a separate publication for every brand here. Substack supports multiple publications under one account — just click your profile → "Create new publication."
            </div>
          </div>
        </div>
        {brands.length === 0 ? (
          <div className="border-2 border-dashed border-[#E2E4E9] p-12 text-center">
            <p className="font-display font-bold text-2xl">No brands yet</p>
            <p className="text-sm text-[#52525B] mt-2">Add a brand first, then come back to set up its Substack.</p>
            <Link to="/brands" className="mt-4 inline-block bg-[#0A0A0B] text-white px-5 py-3 text-sm">Add a brand</Link>
          </div>
        ) : (
          <div className="space-y-2">
            {brands.map((b) => (
              <button
                key={b.brand_id}
                onClick={() => pickBrand(b.brand_id)}
                data-testid={`pick-brand-${b.brand_id}`}
                className="w-full border border-[#E2E4E9] hover:border-black hover:bg-[#F4F5F6] p-4 flex items-center justify-between transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10" style={{ background: b.color }} />
                  <div className="text-left">
                    <div className="font-display font-bold text-lg tracking-tight">{b.name}</div>
                    <div className="text-xs mono uppercase text-[#A1A1AA] mt-1">{b.is_client ? "Client" : "Owned"} · {b.substack_publication_url ? "substack linked" : "substack not linked"}</div>
                  </div>
                </div>
                <ChevronRight size={18} />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (!state) return <div className="text-eyebrow">Loading…</div>;
  const currentBrand = brands.find((b) => b.brand_id === brandId);

  return (
    <div data-testid="onboarding-page" className="max-w-3xl">
      <button onClick={() => navigate("/onboarding")} className="text-eyebrow mb-4 hover:text-[#002FA7]" data-testid="back-to-brand-picker">
        ← Pick a different brand
      </button>
      <div className="mb-8">
        <div className="text-eyebrow mb-3">Substack Setup · {currentBrand?.name}</div>
        <h1 className="font-display font-black text-5xl tracking-tighter">{currentBrand?.name}'s Publication</h1>
        <p className="text-[#52525B] mt-3">Build a Substack <strong>just for this brand</strong>. Your place is saved automatically as you check off each step.</p>
      </div>

      <div className="border-l-4 border-[#F59E0B] bg-[#FFF7ED] p-3 mb-6 text-xs">
        <strong>Important:</strong> Each brand gets its own Substack publication. Don't mix this brand's content with any other brand's publication.
      </div>

      {state.has_substack === null && (
        <div className="border-2 border-black p-8 brutal-shadow-lg mb-8">
          <div className="text-eyebrow mb-2">Do you already have a Substack publication for {currentBrand?.name}?</div>
          <h2 className="font-display font-bold text-3xl tracking-tight mb-6">Skip the steps you've done.</h2>
          <div className="flex gap-3">
            <button onClick={() => setHasSubstack(true)} data-testid="has-substack-yes" className="bg-[#0A0A0B] text-white px-5 py-3 text-sm">Yes — this brand already has one</button>
            <button onClick={() => setHasSubstack(false)} data-testid="has-substack-no" className="border border-black px-5 py-3 text-sm hover:bg-[#F4F5F6]">No — walk me through it</button>
          </div>
        </div>
      )}

      {state.has_substack !== null && (
        <div className="space-y-3">
          <button onClick={() => setHasSubstack(null)} className="text-eyebrow mb-2">← Change answer</button>
          {state.steps.map((s, i) => {
            const done = (state.completed_steps || []).includes(s.id);
            const active = !done && i === (state.completed_steps?.length || 0);
            return (
              <div key={s.id} data-testid={`step-${s.id}`} className={`border ${active ? "border-[#0A0A0B] brutal-shadow" : done ? "border-[#059669]" : "border-[#E2E4E9]"} p-6`}>
                <div className="flex items-start gap-4">
                  <button onClick={() => toggleStep(i)} data-testid={`toggle-step-${s.id}`} className="mt-1">
                    {done ? <CheckCircle size={24} className="text-[#059669]" fill="#059669" stroke="white" /> : <Circle size={24} className="text-[#A1A1AA]" />}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs mono text-[#A1A1AA]">STEP {i + 1}/{state.steps.length}</span>
                      {active && <span className="text-xs mono text-[#002FA7]">CURRENT</span>}
                    </div>
                    <h3 className={`font-display font-bold text-2xl tracking-tight mt-1 ${done ? "line-through text-[#A1A1AA]" : ""}`}>{s.title}</h3>
                    <p className="text-sm text-[#52525B] mt-2 whitespace-pre-wrap">{s.detail}</p>
                    {s.url && (
                      <a href={s.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs mono uppercase text-[#002FA7] hover:underline" data-testid={`step-link-${s.id}`}>
                        Open <ArrowSquareOut size={12} />
                      </a>
                    )}
                    {(s.id === "posting_email" || s.id === "connect_substack") && (
                      <Link to={`/brands/${brandId}`} className="mt-3 ml-2 inline-flex items-center gap-1 text-xs mono uppercase text-[#0A0A0B] hover:underline">
                        Open brand settings →
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
